import { ROBINHOOD_CHAIN_ID, robinhoodChain } from '@legate/shared';
import type { Chain } from 'viem';
import { createConfig, http } from 'wagmi';
import { injected } from 'wagmi/connectors';

/**
 * Wallet connectivity for Robinhood Chain (4663). One chain, one connector: the
 * browser extension the visitor already has. WalletConnect would need a project
 * id and a relay round-trip, which is not a dependency a sign-in button should
 * carry.
 *
 * Signing in needs no RPC at all — the message is signed by the extension and
 * verified by the API. The RPC URL matters at exactly one moment: when the
 * wallet does not know chain 4663 yet and has to be told where the node is. That
 * is why `chainRpcUrl()` is async and asked at that moment, while the config
 * itself is built synchronously from the last known good value.
 */

/** The local Anvil fork, which is the answer in development anyway. */
export const FALLBACK_RPC_URL = 'http://127.0.0.1:8545';

const RPC_CACHE_KEY = 'legate.chain.rpcUrl';

function readCachedRpcUrl(): string {
  if (typeof localStorage === 'undefined') return FALLBACK_RPC_URL;
  try {
    return localStorage.getItem(RPC_CACHE_KEY) ?? FALLBACK_RPC_URL;
  } catch {
    return FALLBACK_RPC_URL;
  }
}

function cacheRpcUrl(url: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(RPC_CACHE_KEY, url);
  } catch {
    /* storage disabled — the fetched value still serves this page load */
  }
}

let inflight: Promise<string> | null = null;

/**
 * The node the API is actually talking to, from `GET /api/chain/info`. Falls
 * back to the local fork so a wallet can still be pointed at a chain when the
 * API is unreachable — a network prompt with no RPC in it is a dead end.
 */
export async function chainRpcUrl(): Promise<string> {
  inflight ??= (async () => {
    try {
      const response = await fetch('/api/chain/info', { headers: { Accept: 'application/json' } });
      if (!response.ok) return readCachedRpcUrl();
      const info = (await response.json()) as { rpcUrl?: unknown };
      if (typeof info.rpcUrl !== 'string' || info.rpcUrl.length === 0) return readCachedRpcUrl();
      cacheRpcUrl(info.rpcUrl);
      return info.rpcUrl;
    } catch {
      return readCachedRpcUrl();
    } finally {
      // One failure must not pin the fallback for the rest of the session.
      inflight = null;
    }
  })();
  return inflight;
}

const rpcUrl = readCachedRpcUrl();

export const robinhood: Chain = robinhoodChain(rpcUrl);

export const wagmiConfig = createConfig({
  chains: [robinhood],
  connectors: [injected({ shimDisconnect: true })],
  transports: { [ROBINHOOD_CHAIN_ID]: http(rpcUrl) },
});

// Warms the cache for the next config build without delaying this one.
void chainRpcUrl();

declare module 'wagmi' {
  interface Register {
    config: typeof wagmiConfig;
  }
}
